import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useScrollReveal } from "../hooks/useScrollReveal";
import { SectionEyebrow } from "./SectionEyebrow";
import { Play, Pause, SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";

const VIDEOS = [
  { src: "/videos/v1.mp4", poster: "/videos/v1-poster.jpg" },
  { src: "/videos/v2.mp4", poster: "/videos/v2-poster.jpg" },
  { src: "/videos/v3.mp4", poster: "/videos/v3-poster.jpg" },
];

const VideoCard = ({ src, poster, label, i }) => {
  const { ref, inView } = useScrollReveal({ threshold: 0.2 });
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <div
      ref={ref}
      data-testid={`video-card-${i}`}
      style={{ animationDelay: `${i * 120}ms` }}
      className={`reveal ${inView ? "in-view" : ""} group relative overflow-hidden bg-[#3E5239] aspect-[9/16] cursor-pointer`}
      onClick={toggle}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        playsInline
        muted
        loop
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
      />
      {/* dark gradient */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#1F2A1B]/70 via-transparent to-transparent pointer-events-none" />

      {/* play button overlay */}
      <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${playing ? "opacity-0 group-hover:opacity-100" : "opacity-100"}`}>
        <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center shadow-2xl transform transition-transform duration-300 group-hover:scale-110">
          {playing ? (
            <Pause size={26} weight="fill" className="text-[#3E5239]" />
          ) : (
            <Play size={28} weight="fill" className="text-[#3E5239] ml-1" />
          )}
        </div>
      </div>

      {/* mute toggle */}
      <button
        type="button"
        onClick={toggleMute}
        data-testid={`video-mute-${i}`}
        className="absolute top-4 right-4 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors"
        aria-label="toggle sound"
      >
        {muted ? <SpeakerSlash size={16} weight="bold" /> : <SpeakerHigh size={16} weight="bold" />}
      </button>

      {/* bottom label */}
      <div className="absolute bottom-0 left-0 right-0 p-5 lg:p-6">
        <div className="overline text-white/80 mb-1">0{i + 1}</div>
        <div className="font-serif text-base lg:text-lg text-white font-light leading-snug">{label}</div>
      </div>
    </div>
  );
};

export const VideoShowcase = () => {
  const { t } = useTranslation();
  const labels = t("videos.items", { returnObjects: true }) || [];

  return (
    <section id="videos" data-testid="videos-section" className="py-24 lg:py-32 bg-[#F6F5F2]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div className="max-w-2xl">
            <SectionEyebrow num="06">{t("videos.eyebrow")}</SectionEyebrow>
            <h2 data-testid="videos-headline" className="font-serif text-4xl lg:text-5xl xl:text-6xl font-light tracking-tight leading-[1.05] text-[#3E5239]">
              {t("videos.title_l1")}
              <br />
              <span className="italic text-[#A7CA99]">{t("videos.title_l2")}</span>
            </h2>
          </div>
          <p className="lg:max-w-sm text-[#6B7A66] font-light leading-relaxed text-sm">{t("videos.intro")}</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {VIDEOS.map((v, i) => (
            <VideoCard key={v.src} src={v.src} poster={v.poster} label={labels[i] || ""} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
